import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { motion } from 'framer-motion';
import { Edit } from 'lucide-react';
import { getDifficultyLabel } from '../../utils/constants';

const goalOptions = [
  { value: 'weight_loss', label: 'Weight Loss' },
  { value: 'muscle_building', label: 'Muscle Building' },
  { value: 'endurance', label: 'Endurance' },
  { value: 'general_fitness', label: 'General Fitness' },
  { value: 'strength', label: 'Strength' },
];

const difficultyOptions = ['beginner', 'intermediate', 'advanced'];

export const ProgramMetaForm = ({ program, updateField }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-card border border-border p-6"
  >
    <h2 className="font-heading text-2xl text-white uppercase tracking-wider mb-6">
      <Edit className="inline h-5 w-5 mr-2 text-primary" />
      Program Details
    </h2>

    <div className="grid md:grid-cols-2 gap-6">
      <div className="md:col-span-2">
        <Label className="text-white uppercase tracking-wider text-sm">Title</Label>
        <Input
          value={program.title}
          onChange={(e) => updateField('title', e.target.value)}
          className="mt-2 bg-black border-border text-white"
          data-testid="program-title-input"
        />
      </div>

      <div className="md:col-span-2">
        <Label className="text-white uppercase tracking-wider text-sm">Description</Label>
        <Textarea
          value={program.description}
          onChange={(e) => updateField('description', e.target.value)}
          className="mt-2 bg-black border-border text-white min-h-[100px]"
          data-testid="program-description-input"
        />
      </div>

      <div>
        <Label className="text-white uppercase tracking-wider text-sm">Goal</Label>
        <select
          value={program.goal}
          onChange={(e) => updateField('goal', e.target.value)}
          className="mt-2 w-full h-10 bg-black border border-border text-white px-3 rounded-md"
          data-testid="program-goal-select"
        >
          {goalOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>

      <div>
        <Label className="text-white uppercase tracking-wider text-sm">Difficulty</Label>
        <select
          value={program.difficulty}
          onChange={(e) => updateField('difficulty', e.target.value)}
          className="mt-2 w-full h-10 bg-black border border-border text-white px-3 rounded-md capitalize"
          data-testid="program-difficulty-select"
        >
          {difficultyOptions.map((opt) => <option key={opt} value={opt} className="capitalize">{getDifficultyLabel(opt)}</option>)}
        </select>
      </div>

      <div>
        <Label className="text-white uppercase tracking-wider text-sm">Price ($)</Label>
        <Input
          type="number" step="0.01"
          value={program.price || ''}
          onChange={(e) => updateField('price', parseFloat(e.target.value) || 0)}
          className="mt-2 bg-black border-border text-white"
          placeholder="0.00"
          data-testid="program-price-input"
        />
      </div>

      <div className="md:col-span-2">
        <Label className="text-white uppercase tracking-wider text-sm">Image URL</Label>
        <Input
          value={program.image_url}
          onChange={(e) => updateField('image_url', e.target.value)}
          className="mt-2 bg-black border-border text-white"
          placeholder="https://..."
          data-testid="program-image-input"
        />
        {program.image_url && (
          <img src={program.image_url} alt="Preview" className="mt-2 h-32 w-full object-cover border border-border" />
        )}
      </div>

      <div className="md:col-span-2 grid sm:grid-cols-2 gap-3">
        <label className="flex items-center gap-3 cursor-pointer bg-black/30 border border-border rounded-md p-3 hover:border-white/30 transition-colors">
          <input
            type="checkbox"
            checked={program.premium_accessible || false}
            onChange={(e) => updateField('premium_accessible', e.target.checked)}
            className="h-5 w-5 accent-primary"
            data-testid="premium-accessible-toggle"
          />
          <div>
            <span className="text-white uppercase tracking-wider text-sm font-heading">Included with Premium</span>
            <p className="text-muted-foreground text-xs mt-0.5">Premium members get this free (no separate purchase)</p>
          </div>
        </label>

        <label className="flex items-center gap-3 cursor-pointer bg-black/30 border border-border rounded-md p-3 hover:border-primary/40 transition-colors">
          <input
            type="checkbox"
            checked={program.premium_only || false}
            onChange={(e) => updateField('premium_only', e.target.checked)}
            className="h-5 w-5 accent-primary"
            data-testid="premium-only-toggle"
          />
          <div>
            <span className="text-primary uppercase tracking-wider text-sm font-heading">🔒 Premium Only (Locked)</span>
            <p className="text-muted-foreground text-xs mt-0.5">Non-premium users see a lock badge instead of "Get Started"</p>
          </div>
        </label>
      </div>
    </div>
  </motion.div>
);
